import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  approveWorkSession,
  listWorkSessions,
  pauseWorkSession,
  rejectWorkSession,
  resumeWorkSession,
  startWorkSession,
  stopWorkSession,
} from '../../../api/workSessionsApi';
import { WorkSessionsFilters } from '../../../interfaces/WorkSession';
import { queryKeys } from '../queryKeys';

export const useWorkSessionsQuery = (filters: WorkSessionsFilters, enabled = true) => {
  return useQuery({
    queryKey: queryKeys.workSessions.list(filters as Record<string, unknown>),
    queryFn: () => listWorkSessions(filters),
    enabled,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
};

export const useStartWorkSessionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    // at-99: `esteiraId` opcional — em qual esteira a hora é apontada. Ausente = a principal.
    mutationFn: ({
      carId,
      password,
      esteiraId,
    }: {
      carId: string;
      password: string;
      esteiraId?: string;
    }) => startWorkSession(carId, password, esteiraId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.workSessions.all });
      queryClient.invalidateQueries({ queryKey: ['audit', 'car-detail'] });
    },
  });
};

export const useStopWorkSessionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    // at-46: `servicoConcluido` declara o desfecho do serviço da esteira (ver stopWorkSession).
    mutationFn: ({
      id,
      password,
      servicoConcluido,
      pausaObservacao,
      motivoParadaId,
    }: {
      id: string;
      password: string;
      servicoConcluido?: boolean;
      pausaObservacao?: string;
      motivoParadaId?: string;
    }) => stopWorkSession(id, password, servicoConcluido, pausaObservacao, motivoParadaId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.workSessions.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.cars.all });
      queryClient.invalidateQueries({ queryKey: ['audit', 'car-detail'] });
    },
  });
};

export const usePauseWorkSessionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason, password }: { id: string; reason: string; password: string }) =>
      pauseWorkSession(id, reason, password),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.workSessions.all });
      queryClient.invalidateQueries({ queryKey: ['audit', 'car-detail'] });
    },
  });
};

export const useResumeWorkSessionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) => resumeWorkSession(id, password),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.workSessions.all });
      queryClient.invalidateQueries({ queryKey: ['audit', 'car-detail'] });
    },
  });
};

export const useApproveWorkSessionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => approveWorkSession(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.workSessions.all });
    },
  });
};

export const useRejectWorkSessionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, rejectionReason }: { id: string; rejectionReason?: string }) =>
      rejectWorkSession(id, rejectionReason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.workSessions.all });
    },
  });
};
